import { config } from '@/forms/config';
import type {
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
} from '@/forms/typings';
import type { OfficeHoursFormField } from '@/forms/typings';
import { generateValidations } from '@/forms/utils/generateValidations';
import { registerOfficeHoursRangeValidation } from '@/forms/utils/createOfficeHoursRangeYup';

import type { FormFieldModuleContext } from '../registry/field-module.types';

export function buildOfficeHoursInitialState(
  field: FormField,
  values: FormInitialValues,
  validations: FormValidations,
  conditions: FormConditionalValidation[] | undefined,
  ctx: FormFieldModuleContext,
): { initialValues: FormInitialValues; validationFields: FormValidations } {
  let initialValues: FormInitialValues = { ...values };
  let validationFields: FormValidations = { ...validations };

  const f = field as OfficeHoursFormField;

  if (f.notApplicableField) {
    initialValues[f.notApplicableField.fieldId] = f.notApplicableField.value;
  }

  const shouldValidateFieldId = `${f.fieldId}${config.shouldValidateFieldSuffix}`;

  // Seeded so the first validation pass sees the real gate value, rather than relying on
  // OfficeHoursPresenter's useShouldValidateField effect having run first.
  initialValues[shouldValidateFieldId] = !f.notApplicableField?.value;

  const rangeConditions: FormConditionalValidation[] = [
    { type: 'should-validate', relatedFieldId: shouldValidateFieldId },
    ...(conditions ?? []),
  ];

  for (const officeHoursFieldDay of Object.entries(f.days)) {
    for (const officeHoursField of officeHoursFieldDay[1].fields) {
      initialValues[officeHoursField.fieldId] = officeHoursField.value;

      const schema = generateValidations(officeHoursField, conditions);

      if (!schema) continue;

      validationFields[officeHoursField.fieldId] = schema;
    }

    registerOfficeHoursRangeValidation(
      validationFields,
      officeHoursFieldDay[1].fields,
      rangeConditions,
    );
  }

  if (f.naDetails && f.notApplicableField) {
    const combinedConditions: FormConditionalValidation[] = [
      { type: 'not-applicable', relatedFieldId: f.notApplicableField.fieldId },
      ...(conditions ?? []),
    ];

    const naValuesAndValidations = ctx.recurse(
      f.naDetails,
      initialValues,
      validationFields,
      combinedConditions,
    );

    initialValues = {
      ...initialValues,
      ...naValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...naValuesAndValidations.validationFields,
    };
  }

  return { initialValues, validationFields };
}
